import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

const DEFAULT_SETTINGS = {
  site_name: 'Itronix Electronics',
  tagline: 'Auto Electronics Solutions',
  logo_url: '',
  primary_color: '#dc2626',
  phone: '+973 13619991',
  email: 'info@itronixbh.com',
  address: 'Shop 589B, Block 646, Rd No 4541, Al Nuwaidrat, Bahrain',
  hours: '10AM - 1PM, 4PM - 10PM (Friday Closed)',
  facebook: 'https://www.facebook.com/itronixbh',
  instagram: 'http://instagram.com/itronixbh',
  youtube: 'https://www.youtube.com/channel/UCrVPQRoXdCiF8bwk4SwYLCA',
  whatsapp: '97313619991',
  meta_title: 'Itronix Electronics - Premium Car Electronics & Android Screens',
  meta_description: 'Itronix Electronics in Bahrain specializing in Android car screens, sound systems, LED lighting, backup cameras and professional installation services.',
  keywords: 'car electronics bahrain, android car screen, car audio bahrain, itronix, car accessories bahrain',
  map_lat: 26.1,
  map_lng: 50.5,
  map_zoom: 15,
  recaptcha_site_key: '6LeFJV0sAAAAAE4BcDBptizS6urCQ6yeyYIVOoXw',
  contact_cards: [{"icon":"map","title":{"en":"Our Location","ar":"موقعنا"},"content":{"en":"Shop 589B, Block 646, Rd No 4541, Al Nuwaidrat, Bahrain","ar":"محل 589، طريق 4541، مجمع 646، النويدرات، البحرين"},"sub_content":{"en":"","ar":""},"action":"https://www.google.com/maps/search/Itronix%20Electronics%2C%20Shop%20589B%2C%20Block%20646%2C%20Rd%20No%204541%2C%20Al%20Nuwaidrat%2C%20Bahrain","sub_action":"","action_label":{"en":"Open in Maps","ar":"افتح في الخرائط"},"order":1},{"icon":"phone","title":{"en":"Phone Number","ar":"رقم الهاتف"},"content":{"en":"+973 13619991","ar":"13619991 973+"},"sub_content":{"en":"","ar":"info@itronixbh.com"},"action":"tel:+97313619991","sub_action":"","action_label":{"en":"Call Now","ar":"اتصل الآن"},"order":2},{"icon":"clock","title":{"en":"Working Hours","ar":"ساعات العمل"},"content":{"en":"10AM - 1PM, 4PM - 10PM (Friday Closed)","ar":"10 ص - 1 م، 4 م - 10 م (الجمعة مغلق)"},"sub_content":{"en":"","ar":""},"action":"","sub_action":"","action_label":{"en":"","ar":""},"order":3},{"title":{"en":"Shop location video","ar":"موقع المحل فيديو"},"description":"Get the shop direction by video","link":"","button_text":"","icon":"video","order":4,"action":"https://youtu.be/LdBpJ-MuBaE","content":{"en":"Video for shop location direction","ar":"فيديو يوضح موقع المتجر"},"action_label":{"en":"Watch now","ar":"شاهد الآن"}},{"icon":"email","title":{"en":"Sales and General Inquiries","ar":"المبيعات والاستفسارات العامة"},"content":{"en":"info@itronixbh.com","ar":"info@itronixbh.com"},"sub_content":{"en":"","ar":""},"action":"mailto:info@itronixbh.com","sub_action":"","action_label":{"en":"Email us","ar":"راسلنا"},"order":5},{"title":{"en":"Complaints & Suggestions","ar":"للشكاوي والاقتراحات"},"description":"Reach us for complaints and suggestions","link":"https://wa.me/97339544612","button_text":"","icon":"whatsapp","order":6,"action":"https://wa.me/97339544612","sub_action":"mailto:complaint@itronixbh.com","content":{"en":"","ar":""},"sub_content":{"en":"complaint@itronixbh.com","ar":"complaint@itronixbh.com"},"action_label":{"en":"Chat now","ar":"دردشة الآن"}}]
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const { i18n } = useTranslation();

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSettings(prev => ({ ...DEFAULT_SETTINGS, ...response.data }));
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const refreshSettings = () => loadSettings();

  // Apply SEO meta tags to document head (language-aware)
  useEffect(() => {
    if (!loaded) return;
    if (document.documentElement.hasAttribute('data-page-seo-active')) return;

    const lang = i18n.language?.substring(0, 2) || 'en';
    const isAr = lang === 'ar';
    const desc = (isAr && settings.meta_description_ar) ? settings.meta_description_ar : settings.meta_description;

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setMeta('keywords', settings.keywords);
  }, [loaded, settings, i18n.language]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};
